import type { SubscribeUiQuery } from "@/Modules/Models/Domain/Subscription";
import type { ApiError } from "@/Services/Api/Models";
import type { SubscribeApiQuery, SubscriptionApi } from "@/Modules/Models/Api/Subscription";
import type { Subscription } from "@/Modules/Models/Domain/Subscription";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import { subscribeToPlan } from "@/Services/TherapyApi/Subscriptions";

import useAppStore from "@/Modules/App/Store";
import useSubscriptionStore from "@/Modules/Subscription/Store";

import camelize from "@/Utils/Camelize";

const getErrorMessage = (errorCode: ApiErrorCodes | string, isExtra: boolean): string => {
  if (isExtra) {
    return "user.settings.subscription.sessions_extra.error.message";
  }

  switch (errorCode) {
    case ApiErrorCodes.CHANGE_SUBSCRIPTION_ERROR:
    case ApiErrorCodes.CREATE_SUBSCRIPTION_ERROR:
      return "user.settings.subscription.payment.card_error.generic_decline";

    case ApiErrorCodes.INVALID_REDUCED_PLAN:
      return "there_was_an_error_connecting_to_the_server_please_try_again_later";

    default:
      return "user.settings.subscription.invalid_reduced_plan";
  }
};

const subscriptionApiToDomain = (subscriptionResponse: SubscriptionApi): Subscription => {
  return camelize<SubscriptionApi>(subscriptionResponse) as Subscription;
};

const subscriptionDomainToApi = (subscriptionUiQuery: SubscribeUiQuery): SubscribeApiQuery => {
  if (subscriptionUiQuery.promoPlanId) {
    return {
      plan_uuid: subscriptionUiQuery.planId,
      user_uuid: subscriptionUiQuery.userId,
      promo_plan_uuid: subscriptionUiQuery.promoPlanId,
    };
  }

  return {
    plan_uuid: subscriptionUiQuery.planId,
    user_uuid: subscriptionUiQuery.userId,
  };
};

async function subscribeToPlanAH(
  subscribeUiQuery: SubscribeUiQuery,
  isExtra = false
): Promise<Subscription | undefined> {
  const appStore = useAppStore();
  const subscriptionStore = useSubscriptionStore();

  subscriptionStore.isLoading = true;

  try {
    const subscriptionResponse = await subscribeToPlan(subscriptionDomainToApi(subscribeUiQuery));

    const subscription = subscriptionApiToDomain(subscriptionResponse);

    subscriptionStore.subscription = subscription;

    if (isExtra) {
      appStore.showSnackbar({
        type: NotificationType.SUCCESS,
        message: "user.settings.subscription.sessions_extra.success.message",
      });
    }

    return subscription;
  } catch (error) {
    const apiError = error as ApiError;
    const message = getErrorMessage(apiError.error_code, isExtra);

    appStore.showSnackbar({
      type: NotificationType.ERROR,
      message,
    });

    subscriptionStore.notification = {
      type: NotificationType.ERROR,
      code: apiError.error_code,
      message,
    };

    return undefined;
  } finally {
    subscriptionStore.isLoading = false;
  }
}

export default subscribeToPlanAH;
